/**
 * Google Analytics events — single source of truth. snake_case, since GA4 event
 * names must be letters/numbers/underscores (no spaces). Used with `trackEvent`.
 */
export const ANALYTICS_EVENTS = {
  QUIZ_CONFIG_MISSING: 'quiz_config_missing',
  PURCHASE_PAGE_VIEWED: 'purchase_page_viewed',
  // GA4-standard names where one exists (purchase / begin_checkout / sign_up).
  BEGIN_CHECKOUT: 'begin_checkout',
  PAYMENT_METHOD_SELECTED: 'payment_method_selected',
  PURCHASE: 'purchase',
  PAYMENT_FAILED: 'payment_failed',
  SIGN_IN_VIEWED: 'sign_in_viewed',
  SIGN_UP: 'sign_up',
  SUCCESS_PAGE_VIEWED: 'success_page_viewed',
  PAGE_VIEW: 'page_view',

  // ── Named quiz-funnel events ───────────────────────────────────────────────
  // Sent client-side to GA4 verbatim (`{Funnel}_Quiz_*` / `{Funnel}_Sub_Screen_*`).
  // Names are shortened for Singular's 32-char event-name limit:
  // `Settlement` → `Settl`, `Onboarding` → `Onb`, `Sub_Button` → `Sub_Btn`.
  // Do not rename without the brief.

  // Onboarding (7)
  SETTLEMENT_ONBOARDING_LOADED: 'Settl_Onb_Loaded',
  SETTLEMENT_ONBOARDING_1OWES_YOU: 'Settl_Onb_1Owes_You',
  SETTLEMENT_ONBOARDING_2SETTLEMENTS: 'Settl_Onb_2Settlements',
  SETTLEMENT_ONBOARDING_3FILE_ONCE: 'Settl_Onb_3File_Once',
  SETTLEMENT_ONBOARDING_4FINES: 'Settl_Onb_4Fines',
  SETTLEMENT_ONBOARDING_5UNCLAIMED: 'Settl_Onb_5Unclaimed',
  SETTLEMENT_ONBOARDING_6FIND_OUT: 'Settl_Onb_6Find_Out',

  // Settlement quiz (16)
  SETTLEMENT_QUIZ_LOADED: 'Settl_Quiz_Loaded',
  SETTLEMENT_QUIZ_1GENDER: 'Settl_Quiz_1Gender',
  SETTLEMENT_QUIZ_2FACEBOOK: 'Settl_Quiz_2Facebook',
  SETTLEMENT_QUIZ_3VISA: 'Settl_Quiz_3Visa',
  SETTLEMENT_QUIZ_4YAHOO: 'Settl_Quiz_4Yahoo',
  SETTLEMENT_QUIZ_5TMOBILE: 'Settl_Quiz_5TMobile',
  SETTLEMENT_QUIZ_6CREDIT: 'Settl_Quiz_6Credit',
  SETTLEMENT_QUIZ_723ANDME: 'Settl_Quiz_723andMe',
  SETTLEMENT_QUIZ_8AMAZON: 'Settl_Quiz_8Amazon',
  SETTLEMENT_QUIZ_9MARRIOTT: 'Settl_Quiz_9Marriott',
  SETTLEMENT_QUIZ_10GOOGLE: 'Settl_Quiz_10Google',
  SETTLEMENT_QUIZ_PROCESSING: 'Settl_Quiz_Processing',
  SETTLEMENT_QUIZ_EMAIL: 'Settl_Quiz_Email',
  SETTLEMENT_QUIZ_CLAIMS: 'Settl_Quiz_Claims',
  SETTLEMENT_QUIZ_PROFILE: 'Settl_Quiz_Profile',
  SETTLEMENT_QUIZ_SIGNING: 'Settl_Quiz_Signing',

  // Paywall (3)
  SETTLEMENT_SUB_SCREEN_SHOWN: 'Settl_Sub_Screen_Shown',
  SETTLEMENT_SUB_SCREEN_BUTTON_CLICK: 'Settl_Sub_Screen_Button_Click',
  SETTLEMENT_SUB_SCREEN_SUB_BUTTON_CLICK: 'Settl_Sub_Screen_Sub_Btn_Click',

  // Additional offers (10) — same `{Funnel}_{Step}_{Shown|Button_Click|Skip}` scheme.
  SETTLEMENT_PRIORITY_SHOWN: 'Settl_Priority_Shown',
  SETTLEMENT_PRIORITY_BUTTON_CLICK: 'Settl_Priority_Button_Click',
  SETTLEMENT_PRIORITY_SKIP: 'Settl_Priority_Skip',
  SETTLEMENT_COVERAGE_SHOWN: 'Settl_Coverage_Shown',
  SETTLEMENT_COVERAGE_BUTTON_CLICK: 'Settl_Coverage_Button_Click',
  SETTLEMENT_COVERAGE_SKIP: 'Settl_Coverage_Skip',
  SETTLEMENT_REGISTER_SHOWN: 'Settl_Register_Shown',
  SETTLEMENT_REGISTER_BUTTON_CLICK: 'Settl_Register_Button_Click',
  SETTLEMENT_GET_APP_SHOWN: 'Settl_Get_App_Shown',
  SETTLEMENT_GET_APP_BUTTON_CLICK: 'Settl_Get_App_Button_Click',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/**
 * Attribution payload sent to the subscriptions backend.
 * Field names mirror the Subtrack/origene contract exactly — only these keys
 * are recognized server-side. Falsy values are stripped before sending.
 */
export interface AttributionData {
  project: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  adset_id: string | null;
  adset_name: string | null;
  ad_id: string | null;
  ad_name: string | null;
  media_source: string | null;
  config: string | null;
  fb_pixel: string | null;
  fbc: string | null;
  fbp: string | null;
  referer: string | null;
  event_source_url: string | null;
  client_user_agent: string | null;
  idfm: string | null;
  userAgent_1: string | null;
  userAgent_2: string | null;
  userAgent_3: string | null;
  userAgent_4: string | null;
  /** Enriched asynchronously from the geo lookup. */
  country_code: string | null;
  client_ip_address: string | null;
}

export interface AnalyticsPayload {
  eventName: AnalyticsEventName;
  userId?: string;
  anonymousId?: string;
  sessionId?: string;
  productId?: string;
  /** Subscription/order id — sent to GA4 as `transaction_id` (purchase dedup). */
  transactionId?: string;
  price?: number;
  currency?: string;
  paymentMethod?: 'card' | 'apple_pay' | 'google_pay';
  /** Quiz step selection: male | female | yes | no | skip. */
  answer?: string;
  attribution?: AttributionData;
  metadata?: Record<string, unknown>;
}

export interface AnalyticsProvider {
  name: string;
  init(): void;
  track(event: AnalyticsEventName, payload: AnalyticsPayload): void;
  page(path: string): void;
}
